import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { getChildEdgeTypeId, SYSTEM } from '@iotv/datamodel';
import { Milliseconds } from '@iotv/iotv-v3-types';
import { Alert, Box, Button, Card, Grid, Modal, Typography } from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { AppMainViewContext } from '../../ContextProviders/AppContextProvider';
import AppDao from '../../data/AppDao';
import { ConsumerDao } from '../../data/ConsumerDAO';
import { DeviceDAO } from '../../data/SystemManagementDAO';
import { SteppingLoadingAnimation } from '../../Popup/Loading';
import { Severity } from '../../types/AppTypes';
import { AddCancelDelete } from '../common/ObjectGenerics';
import { DeviceLinkContext } from './DeviceLinkContext';
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const MIN_WAIT_MS = Milliseconds.SECOND / 2;
var LINKING_TRANSACTION;
(function (LINKING_TRANSACTION) {
    LINKING_TRANSACTION["Updating"] = "Updating";
    LINKING_TRANSACTION["MovingDown"] = "Moving device to customer";
    LINKING_TRANSACTION["Linking"] = "Linking device to entity";
    LINKING_TRANSACTION["Unlinking"] = "Unlinking old device";
    LINKING_TRANSACTION["MovingUp"] = "Returning old device";
    LINKING_TRANSACTION["AreaUpdate"] = "Updating areas";
})(LINKING_TRANSACTION || (LINKING_TRANSACTION = {}));
var LINK_TYPE;
(function (LINK_TYPE) {
    LINK_TYPE["LINK"] = "LINK";
    LINK_TYPE["REPLACE"] = "REPLACE";
})(LINK_TYPE || (LINK_TYPE = {}));
const LinkingDBTransactions = (props) => {
    const { clear } = props;
    const [{ contextCustomer, contextUser, isSmallView }] = useContext(AppMainViewContext);
    const { choosenEntity, selectedGroup, updateLocation, location, allowEntities, device, relevantAreas, unlinkDevice } = useContext(DeviceLinkContext);
    const [{ UserFunctions }] = useContext(AppMainViewContext);
    const [linkType, setLinkType] = useState(undefined);
    const [willUpdateLocation, setWillUpdateLocation] = useState(true);
    const [alert, setAlert] = useState('');
    const [saving, setSaving] = useState(false);
    const [progression, setProgress] = useState(0);
    const [promiseNames, setPromiseNames] = useState([]);
    useEffect(() => {
        if (updateLocation) {
            setWillUpdateLocation(true);
        }
        else {
            setWillUpdateLocation(false);
        }
    }, [updateLocation, location, choosenEntity]);
    const setSavingSettings = (type) => {
        setLinkType(type);
    };
    const closeModal = () => {
        setLinkType(undefined);
        setAlert('');
    };
    const updateLocationTransaction = (existing, location) => {
        const entityLocationPromise = AppDao.save({ ...existing, location });
        return entityLocationPromise;
    };
    const allocateToCustomer = (customer) => {
        const moveDevicePromise = DeviceDAO.allocateDeviceThroughHierarchy({
            contextCustomer: customer,
            device: device,
            contextUser: contextUser,
            deviceGroup: selectedGroup,
        });
        return moveDevicePromise;
    };
    const allocateDevToEntity = (existing, dev) => {
        const linkEntityPromise = AppDao.link(choosenEntity, device, getChildEdgeTypeId(existing.type, dev.type));
        return linkEntityPromise;
    };
    const unallocateDeviceTransaction = () => {
        const preLinkPromise = AppDao.unlink(choosenEntity, unlinkDevice);
        return preLinkPromise;
    };
    const returnDevice = (apex, current) => {
        const deAllocatingPromise = DeviceDAO.unAllocateDevice({
            contextCustomer: apex,
            device: unlinkDevice,
            contextUser: SYSTEM,
            unAssignmentCustomer: current,
        });
        return deAllocatingPromise;
    };
    const updateAreaTransaction = (existing) => {
        const areaLinkPromises = relevantAreas.map((area) => AppDao.link(area, choosenEntity, getChildEdgeTypeId(area.type, existing.type)));
        return areaLinkPromises;
    };
    const setErrorOnSave = (error) => {
        setProgress(0);
        setPromiseNames([]);
        setSaving(false);
        setAlert(JSON.stringify(error.err));
    };
    const saveFunction = async () => {
        if (choosenEntity && device && contextCustomer && contextUser) {
            setSaving(true);
            // 1 update entity's location
            let apex;
            const promiseList = [];
            if (willUpdateLocation && location) {
                promiseList.push(LINKING_TRANSACTION.Updating);
                setPromiseNames((cur) => [...cur, LINKING_TRANSACTION.Updating]);
            }
            // 2  move device to customer
            promiseList.push(LINKING_TRANSACTION.MovingDown);
            setPromiseNames((cur) => [...cur, LINKING_TRANSACTION.MovingDown]);
            // 3 link device to entity
            promiseList.push(LINKING_TRANSACTION.Linking);
            setPromiseNames((cur) => [...cur, LINKING_TRANSACTION.Linking]);
            // 4 replace
            if (linkType === LINK_TYPE.REPLACE && unlinkDevice) {
                promiseList.push(LINKING_TRANSACTION.Unlinking);
                setPromiseNames((cur) => [...cur, LINKING_TRANSACTION.Unlinking]);
                const apexCustomersRes = await ConsumerDao.getNamedApexItems({ type: 'Customer' });
                if (apexCustomersRes.err === null && apexCustomersRes.data !== null) {
                    apex = apexCustomersRes.data[0];
                    promiseList.push(LINKING_TRANSACTION.MovingUp);
                    setPromiseNames((cur) => [...cur, LINKING_TRANSACTION.MovingUp]);
                }
            }
            // 5 update to area if necessary // updateArea &&
            if (relevantAreas?.length > 0) {
                promiseList.push(LINKING_TRANSACTION.AreaUpdate);
                setPromiseNames((cur) => [...cur, LINKING_TRANSACTION.AreaUpdate]);
            }
            // iterate through promises
            do {
                const currentAction = promiseList.shift();
                let currentRes; // a promise result
                const start = Date.now();
                switch (currentAction) {
                    case LINKING_TRANSACTION.Updating:
                        currentRes = await updateLocationTransaction(choosenEntity, location);
                        break;
                    case LINKING_TRANSACTION.MovingDown:
                        currentRes = await allocateToCustomer(contextCustomer);
                        break;
                    case LINKING_TRANSACTION.Linking:
                        currentRes = await allocateDevToEntity(choosenEntity, device);
                        break;
                    case LINKING_TRANSACTION.Unlinking:
                        currentRes = await unallocateDeviceTransaction();
                        break;
                    case LINKING_TRANSACTION.MovingUp:
                        if (apex) {
                            currentRes = await returnDevice(apex, contextCustomer);
                        }
                        break;
                    case LINKING_TRANSACTION.AreaUpdate:
                        currentRes = await Promise.all(updateAreaTransaction(choosenEntity));
                        break;
                }
                const duration = Date.now() - start;
                // Ensure at least 1000ms elapsed
                const remaining = MIN_WAIT_MS - duration;
                if (remaining > 0) {
                    await sleep(remaining);
                }
                setProgress((cur) => cur + 1);
                if (Array.isArray(currentRes)) {
                    // check if has issue
                    const hasError = currentRes.find((res) => res.err !== null);
                    if (hasError && hasError.err !== null) {
                        setErrorOnSave(hasError.err);
                        return;
                    }
                }
                else if (currentRes && currentRes.err !== null) {
                    setErrorOnSave(currentRes.err);
                    return;
                }
            } while (promiseList.length !== 0);
            setSaving(false);
            setProgress(0);
            setPromiseNames([]);
            const message = {
                id: uuidv4(),
                content: `Successfully Linked Device: ${device.id}`,
                label: '',
                type: 'MessageBar',
                severity: Severity.success,
            };
            UserFunctions.setUserMessage(message);
            setTimeout(() => UserFunctions.dismissUserMessage(message), 3000);
            clear();
            closeModal();
        }
    };
    return (_jsxs("div", { children: [_jsxs(Grid, { id: 'Linking-Saving', container: true, size: { xs: 12 }, spacing: 2, sx: {
                    paddingTop: '10px',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                }, children: [_jsxs(Button, { onClick: () => setSavingSettings(unlinkDevice ? LINK_TYPE.REPLACE : LINK_TYPE.LINK), color: unlinkDevice && 'secondary', fullWidth: true, variant: "contained", disabled: choosenEntity === undefined || !allowEntities, children: [unlinkDevice && 'Replace and ', "Link"] }), _jsx("br", {})] }), choosenEntity && (_jsx(Modal, { open: linkType !== undefined, onClose: closeModal, children: _jsxs(_Fragment, { children: [alert && (_jsx(Box, { sx: { m: 2 }, children: _jsxs(Alert, { variant: "filled", severity: "error", children: ["Error: ", alert] }) })), _jsxs(Card, { sx: {
                                textAlign: 'center',
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                minWidth: isSmallView ? '95vw' : '60vw',
                                minHeight: '35vh',
                                transform: 'translate(-50%, -50%)',
                            }, children: [_jsx("div", { children: _jsxs(Box, { style: { minHeight: '35vh' }, children: [_jsx("br", {}), _jsxs(Typography, { variant: "h5", component: "h5", color: "primary", children: ["Linking Summary for ", _jsx("b", { children: choosenEntity.name })] }), _jsx("br", {}), willUpdateLocation ? (_jsxs(_Fragment, { children: [_jsxs(Typography, { variant: "subtitle1", component: "h6", color: "primary", children: ["Updating ", choosenEntity.type, " location"] }), _jsx(Typography, { variant: "body2", component: "p", color: "primary", children: location && (_jsxs(_Fragment, { children: [_jsxs("b", { children: [" ", choosenEntity.type, " "] }), " will be set to", ' ', _jsxs("u", { children: [location.lat, ", ", location.lng] })] })) }), willUpdateLocation && !updateLocation && (_jsxs(Typography, { variant: "body2", component: "p", color: "primary", children: [choosenEntity.type, " ", _jsx("b", { children: "did not have a location" })] }))] })) : (_jsxs(_Fragment, { children: [_jsx(Typography, { variant: "subtitle1", component: "h6", color: "primary", children: "Keep original location" }), _jsx(Typography, { variant: "body2", component: "p", color: "primary", children: choosenEntity.location && (_jsxs(_Fragment, { children: [_jsxs("b", { children: [" ", choosenEntity.type, " "] }), " will be set to ", choosenEntity.location.lat, ", ", choosenEntity.location.lng] })) })] })), _jsx("br", {}), _jsx(Typography, { variant: "subtitle1", component: "h6", color: "primary", children: linkType === LINK_TYPE.REPLACE ? 'Replacing device link' : 'Adding device link' }), _jsxs(Typography, { variant: "subtitle1", component: "h6", color: "primary", children: ["Device ", _jsx("b", { children: device?.eui }), " will be linked to the ", _jsx("b", { children: choosenEntity.type })] }), linkType === LINK_TYPE.REPLACE && unlinkDevice !== undefined && (_jsxs(Typography, { variant: "subtitle1", component: "h6", color: "primary", children: ["Replacing ", _jsx("b", { children: unlinkDevice.name })] })), _jsx("br", {}), _jsx(Typography, { variant: "subtitle1", component: "h6", color: "primary", children: "Moving Device's Group" }), _jsxs(Typography, { variant: "body2", component: "p", color: "primary", children: ["Device will be moved to ", selectedGroup ? '' : 'the default', " group", _jsx("b", { children: selectedGroup ? `: ${selectedGroup.name}` : '' })] }), _jsx("br", {}), _jsx(AddCancelDelete, { disabled: false, valid: true, cancelFun: () => setSavingSettings(undefined), saveFun: () => saveFunction() })] }) }, "savingSumamry"), _jsx(Modal, { open: saving, onClose: (event, reason) => {
                                        if (reason === 'backdropClick')
                                            return; // Ignore backdrop clicks
                                        closeModal();
                                    }, children: _jsx(SteppingLoadingAnimation, { background: true, current: progression, max: promiseNames.length, message: promiseNames.length > 0 ? promiseNames[progression] : 'Loading' }) })] })] }) }))] }));
};
export default LinkingDBTransactions;
